// Add-a-coach / enrol-an-athlete dialog for a training class (see
// `features/training-classes.md`). The same dialog serves both roles via
// `mode`: in "coach" mode it offers only company athletes marked as coaches; in
// "athlete" mode it offers every company athlete. The roster is fetched fresh
// each time the dialog opens, so athletes and coaches created elsewhere show up
// without a page refresh. Because a company can hold hundreds of athletes, the
// picker is a searchable Autocomplete rather than a plain dropdown.

import { useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import type { Athlete, TrainingClass } from '../domain';
import type { TrainingClassesSnapshot } from '../data/apiClient';
import {
  addTrainingClassCoach,
  addTrainingClassEnrollment,
  fetchAthletes,
} from '../data/apiClient';
import { useTranslation } from '../state/I18nContext';

export type TrainingMemberMode = 'coach' | 'athlete';

interface TrainingClassMemberDialogProps {
  open: boolean;
  mode: TrainingMemberMode;
  trainingClass: TrainingClass;
  /** Personal numbers already filling this role on the class (excluded). */
  takenIds: Set<string>;
  onClose: () => void;
  onSaved: (snapshot: TrainingClassesSnapshot) => void;
}

export function TrainingClassMemberDialog({
  open,
  mode,
  trainingClass,
  takenIds,
  onClose,
  onSaved,
}: TrainingClassMemberDialogProps) {
  const { t } = useTranslation();

  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Athlete | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load a fresh roster whenever the dialog opens, so people created in the
  // Athletes section (or newly marked as coaches) appear immediately.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSelected(null);
    setError(null);
    setLoading(true);
    void (async () => {
      try {
        const roster = await fetchAthletes(trainingClass.companyId);
        if (!cancelled) setAthletes(roster);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : t('admin.trainingClasses.errors.load'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, trainingClass.companyId, t]);

  const eligible = useMemo(
    () =>
      athletes.filter(
        (a) => (mode === 'coach' ? a.isCoach : true) && !takenIds.has(a.personalNumber),
      ),
    [athletes, mode, takenIds],
  );

  const submit = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    try {
      const athleteId = selected.personalNumber;
      const snapshot =
        mode === 'coach'
          ? await addTrainingClassCoach(trainingClass.id, { athleteId })
          : await addTrainingClassEnrollment(trainingClass.id, { athleteId });
      onSaved(snapshot);
      onClose();
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : t('admin.trainingClasses.errors.member'),
      );
    } finally {
      setBusy(false);
    }
  };

  const titleKey =
    mode === 'coach'
      ? 'admin.trainingClasses.memberDialog.coachTitle'
      : 'admin.trainingClasses.memberDialog.athleteTitle';
  const emptyKey =
    mode === 'coach'
      ? 'admin.trainingClasses.memberDialog.noCoaches'
      : 'admin.trainingClasses.memberDialog.noAthletes';

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t(titleKey, { name: trainingClass.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : eligible.length === 0 ? (
            <Alert severity="info">{t(emptyKey)}</Alert>
          ) : (
            <Autocomplete
              options={eligible}
              value={selected}
              onChange={(_e, value) => setSelected(value)}
              getOptionLabel={(a) => `${a.fullName} (${a.personalNumber})`}
              isOptionEqualToValue={(a, b) => a.personalNumber === b.personalNumber}
              noOptionsText={t('admin.trainingClasses.memberDialog.noMatches')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label={t('admin.trainingClasses.memberDialog.athlete')}
                  placeholder={t('admin.trainingClasses.memberDialog.searchPlaceholder')}
                />
              )}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('admin.common.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !selected}
          onClick={() => void submit()}
        >
          {t('admin.common.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
